/**
 * A strong password, made in the browser (after Corly) - the „Sicheres Passwort vorschlagen“ of
 * the password form. It never leaves the page except as the new password itself.
 *
 * Twenty characters of letters, digits and a few symbols that survive every keyboard layout, at
 * least one of each kind. Drawn by `crypto.getRandomValues` with rejection sampling - `% n` alone
 * would make the first characters of an alphabet more likely.
 */
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const SYMBOLS = '!#%+-=?@_';
const ALL = LOWER + UPPER + DIGITS + SYMBOLS;
/** A uniform index below `n` (at most 256). */
function randomIndex(n) {
    const limit = 256 - (256 % n);
    const byte = new Uint8Array(1);
    for (;;) {
        crypto.getRandomValues(byte);
        const value = byte[0] ?? limit;
        if (value < limit)
            return value % n;
    }
}
const pick = (alphabet) => alphabet.charAt(randomIndex(alphabet.length));
export function generatePassword(length = 20) {
    const chars = [pick(LOWER), pick(UPPER), pick(DIGITS), pick(SYMBOLS)];
    while (chars.length < length)
        chars.push(pick(ALL));
    // Shuffled (Fisher-Yates), so the kinds do not always stand in front.
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = randomIndex(i + 1);
        [chars[i], chars[j]] = [chars[j] ?? '', chars[i] ?? ''];
    }
    return chars.join('');
}
/**
 * What the sign-in of Kanos asks of a new password and the browser can check, rule by rule (the
 * realm's policy: `length(12)`, `maxLength(128)`, `notEmail`). The user name and the last three
 * passwords only Keycloak knows - the form says so beside the ticks.
 */
export function passwordChecks(password, email) {
    return [
        { label: 'Mindestens 12 Zeichen', met: password.length >= 12 },
        { label: 'Höchstens 128 Zeichen', met: password.length > 0 && password.length <= 128 },
        {
            label: 'Nicht Ihre E-Mail-Adresse',
            met: password.length > 0 && password.toLowerCase() !== (email ?? '').toLowerCase(),
        },
    ];
}
/**
 * How hard the password is to guess, roughly - length and the kinds of characters - as one of
 * four words. A hint beside the rules, not a rule: Keycloak decides on saving.
 */
export function passwordStrength(password) {
    const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z\d]/].filter((r) => r.test(password)).length;
    if (password.length < 12)
        return 0;
    if (password.length >= 20 || (password.length >= 16 && kinds >= 3))
        return 3;
    return kinds >= 3 || password.length >= 16 ? 2 : 1;
}
//# sourceMappingURL=password.js.map